import React from 'react';
import { AlertCircle, ArrowRight } from 'lucide-react';

/**
 * NextBestAction — surfaces the single most valuable action for a
 * landlord/tenant based on their current state.
 *
 * Design intent: replaces the "6 KPI cards + user has to figure out
 * which one matters" pattern with a single opinionated CTA banner.
 *
 * Props:
 *   title, description — copy
 *   actionText, onAction — CTA
 *   type — "warning" (amber) | "info" (blue) | "success" (emerald)
 *   testid — optional override for the CTA button data-testid
 */
export default function NextBestAction({
  title,
  description,
  actionText,
  onAction,
  type = 'warning',
  testid = 'next-best-action-btn',
  icon: Icon = AlertCircle,
}) {
  const bgColors = {
    warning: 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900',
    info: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900',
    success: 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900',
  };
  const iconColors = {
    warning: 'text-amber-600 dark:text-amber-400',
    info: 'text-blue-600 dark:text-blue-400',
    success: 'text-emerald-600 dark:text-emerald-400',
  };

  return (
    <div
      data-testid="next-best-action-card"
      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border rounded-sm ${bgColors[type]}`}
    >
      <div className="flex items-center space-x-4 mb-4 sm:mb-0">
        <div className={`p-2 rounded-full bg-white dark:bg-black shadow-sm ${iconColors[type]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-foreground">{title}</h4>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <button
        data-testid={testid}
        onClick={onAction}
        className="flex items-center space-x-2 px-4 py-2 bg-foreground text-background hover:opacity-90 transition-opacity rounded-none text-sm font-medium whitespace-nowrap"
      >
        <span>{actionText}</span>
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
